import React, { useEffect, useMemo, useRef } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { Line } from "react-chartjs-2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  LegendItem,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import { isEqual } from "lodash";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  zoomPlugin,
);

// Set global font defaults
ChartJS.defaults.font.family = "JaguarExuberant, Helvetica, Arial, sans-serif";

interface EnergyGraphDeltaProps {
  compareDriver: string;
  yAxisMin?: number;
  yAxisMax?: number;
}

function EnergyGraphDelta({
  compareDriver,
  yAxisMin,
  yAxisMax,
}: EnergyGraphDeltaProps) {
  const chartRef = useRef<ChartJS<"line"> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const tvEnergyData = useSelector(
    (state: RootState) => state.tvEnergy.data,
    isEqual,
  );

  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );

  const currentLap = useSelector(
    (state: RootState) => state.aks.v2.timing?.session?.status?.currentLap,
  );

  const finalLaps = useSelector(
    (state: RootState) => state.aks.v2.timing?.session?.status?.finalLaps,
  );

  const chartData = useMemo(() => {
    if (
      !tvEnergyData ||
      Object.keys(tvEnergyData).length === 0 ||
      !tvEnergyData[compareDriver]
    ) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const compareData = tvEnergyData[compareDriver]?.processedPercentByLap;
    if (!compareData) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const datasets = Object.keys(tvEnergyData)
      .filter((driverKey) => driverKey !== compareDriver)
      .map((driverKey, _) => {
        const driverData = tvEnergyData[driverKey];
        const driverName = driverData.driverName || driverKey;

        const driverInfoEntry =
          driverInfo[driverKey as keyof typeof driverInfo];
        const color = driverInfoEntry?.driverTeamColour || "white";
        const lineStyle = driverInfoEntry?.driverLineStyle || "solid";
        const borderDash = lineStyle === "solid" ? [] : [10, 5];

        const dataPoints: { x: number; y: number }[] = [];

        if (driverData.processedPercentByLap) {
          Object.entries(driverData.processedPercentByLap).forEach(
            ([lapNumber, processedPercent]) => {
              const compareValue =
                (compareData as Record<string, number>)[lapNumber] ?? NaN;
              const lapNum = Number(lapNumber);
              if (
                !isNaN(processedPercent) &&
                !isNaN(compareValue) &&
                !isNaN(lapNum)
              ) {
                dataPoints.push({
                  x: lapNum,
                  y: processedPercent - compareValue,
                });
              }
            },
          );
        }

        return {
          label: `${driverName}`,
          data: dataPoints,
          backgroundColor: color,
          borderColor: color,
          borderWidth: 3,
          pointRadius: 3,
          pointHoverRadius: 6,
          fill: false,
          borderDash: borderDash,
        };
      });

    const referenceDriverInfo =
      driverInfo[compareDriver as keyof typeof driverInfo];
    const referenceColor = referenceDriverInfo?.driverTeamColour || "white";
    const referenceLineStyle = referenceDriverInfo?.driverLineStyle || "solid";
    const referenceBorderDash = referenceLineStyle === "solid" ? [] : [10, 5];
    const referenceLineData: { x: number; y: number }[] = [];
    if (compareData) {
      Object.keys(compareData).forEach((lapNumber) => {
        const lapNum = Number(lapNumber);
        if (lapNum) {
          referenceLineData.push({ x: lapNum, y: 0 });
        }
      });
    }

    const referenceLine = {
      label: `${
        tvEnergyData[compareDriver].driverName || compareDriver
      } (Reference)`,
      data: referenceLineData,
      backgroundColor: referenceColor,
      borderColor: referenceColor,
      borderWidth: 5,
      pointRadius: 3,
      pointHoverRadius: 0,
      fill: false,
      borderDash: referenceBorderDash,
    };

    return { datasets: [...datasets, referenceLine] };
  }, [tvEnergyData, compareDriver, driverInfo]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let animationFrame: number | undefined;
    const resizeChart = () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }

      animationFrame = requestAnimationFrame(() => {
        chartRef.current?.resize();
      });
    };

    resizeChart();

    const resizeObserver = new ResizeObserver(resizeChart);
    resizeObserver.observe(container);
    window.addEventListener("resize", resizeChart);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }

      resizeObserver.disconnect();
      window.removeEventListener("resize", resizeChart);
    };
  }, []);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        display: true,
        position: "top" as const,
        labels: {
          color: "#000000ff",
          usePointStyle: true,
          pointStyle: "line" as const,
          generateLabels: (chart: ChartJS): LegendItem[] => {
            const datasets = chart.data.datasets;
            return datasets.map((dataset, index) => ({
              text: dataset.label ?? "",
              strokeStyle: dataset.borderColor as string,
              lineWidth: (dataset.borderWidth as number) ?? 3,
              lineDash: (dataset as { borderDash?: number[] }).borderDash ?? [],
              hidden: !chart.isDatasetVisible(index),
              datasetIndex: index,
              pointStyle: "line" as const,
            }));
          },
        },
      },
      title: {
        display: true,
        text: `Processed Energy Delta by Lap (vs ${
          tvEnergyData?.[compareDriver]?.driverName || compareDriver
        })`,
        color: "#000000ff",
        font: {
          size: 18,
          weight: "bold" as const,
        },
      },
    },
    scales: {
      x: {
        type: "linear" as const,
        min: 0,
        max:
          currentLap && finalLaps
            ? currentLap === finalLaps + 1
              ? finalLaps
              : currentLap
            : undefined,
        title: {
          display: true,
          text: "End of Lap Number",
          color: "#000000ff",
        },
        ticks: {
          color: "#000000ff",
          stepSize: 1,
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
      },
      y: {
        title: {
          display: true,
          text: "Delta Processed Energy (%)",
          color: "#000000ff",
        },
        ticks: {
          color: "#000000ff",
          stepSize: 1,
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
        min: yAxisMin ?? -4,
        max: yAxisMax ?? 4,
      },
    },
    interaction: {
      mode: "point" as const,
      axis: "x" as const,
      intersect: false,
    },
    elements: {
      point: {
        backgroundColor: "#000000ff",
        borderWidth: 2,
      },
    },
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <Line ref={chartRef} data={chartData} options={chartOptions} />
    </div>
  );
}

export default EnergyGraphDelta;
